import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router';
import { ApiTypes } from '@/types/server/api.types';
import { logError } from '@/utils/logger.utils';

// Type for Google OAuth response
interface GoogleOAuthResponse {
	error?: string;
	code?: string;
}

// Type for Google OAuth client
interface GoogleOAuthClient {
	requestCode: () => void;
}

interface UseGoogleLoginProps {
	setIsUserLoggedIn: (isUserLoggedIn: boolean) => void;
	redirectTo?: string;
	setUserDetails: (
		userDetails: ApiTypes.TGetCandidateDetailsResponse['data']
	) => void;
}

interface UseGoogleLoginReturn {
	isLoading: boolean;
	initiateGoogleLogin: () => void;
	isGoogleAvailable: boolean;
}

export const useGoogleLogin = ({
	setIsUserLoggedIn,
	redirectTo = '/',
	setUserDetails,
}: UseGoogleLoginProps): UseGoogleLoginReturn => {
	const clientRef = useRef<GoogleOAuthClient | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const [isGoogleAvailable, setIsGoogleAvailable] = useState(false);
	const navigate = useNavigate();

	const handleLoginWithServer = async (googleAuthorizationCode: string) => {
		try {
			setIsLoading(true);

			const response = await fetch(
				`${import.meta.env.VITE_SERVER_API_PREFIX}/candidates/login`,
				{
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
					},
					body: JSON.stringify({ googleAuthorizationCode }),
				}
			);

			const data = (await response.json()) as ApiTypes.TLoginCandidateResponse;

			if (data.httpResponseCode !== 200 || !data.data) {
				toast('Error logging in', {
					description:
						data.message ||
						'Login failed. Please refresh the page and try again!',
					id: 'login-error',
				});
				return;
			}

			window.localStorage.setItem('token', data.data.candidateAuthToken);

			setIsUserLoggedIn(true);
			setUserDetails({
				candidateId: data.data.id,
				email: data.data.email,
			});
			navigate(redirectTo);
		} catch (error) {
			logError(error);

			toast('Error logging in', {
				description:
					'Error while logging in. Please check your network or refresh your page and try again.',
				id: 'login-error',
			});
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		if (!window.google?.accounts?.oauth2) {
			toast('Google login unavailable', {
				description:
					'Google login is currently not available. Please try again later.',
				id: 'google-sdk-error',
			});
			setIsGoogleAvailable(false);
			return;
		}

		setIsGoogleAvailable(true);

		clientRef.current = window.google.accounts.oauth2.initCodeClient({
			client_id: import.meta.env.VITE_GOOGLE_CLIENT_ID,
			scope: 'email profile openid',
			callback: (response: GoogleOAuthResponse) => {
				if (response?.error || !response?.code) {
					setIsLoading(false);
					toast('Error logging in', {
						description:
							'Login failed. Please select a Google account to continue.',
						id: 'login-error',
					});
					return;
				}

				handleLoginWithServer(response.code);
			},
			error_callback: () => {
				setIsLoading(false);
				toast('Error logging in', {
					description: 'Network issue or permission denied. Please try again.',
					id: 'login-error',
				});
			},
		});
	}, [setIsUserLoggedIn, navigate, redirectTo, setUserDetails]);

	const initiateGoogleLogin = () => {
		if (isLoading) return;

		if (!clientRef.current) {
			toast('Google login unavailable', {
				description:
					'Google login is not initialized yet. Please try again shortly.',
				id: 'client-ref-missing',
			});
			return;
		}

		setIsLoading(true);
		clientRef.current.requestCode();
	};

	return {
		isLoading,
		initiateGoogleLogin,
		isGoogleAvailable,
	};
};
